<template>
    <div class="card">
        <div class="card-header bg-primary">
            <div class="col-12 col-xl-3 px-0">
                <h3 class="mb-0 text-white">Cart</h3>
            </div>
            <div class="col-12 col-xl-9 px-0 mt-2 mt-xl-0 d-flex justify-content-start justify-content-xl-end">
                <jet-button btnStyle="success" class="btn-sm mr-3" @click.prevent="showAddLineItemModal">Add Line
                    Item</jet-button>
                <jet-button btnStyle="secondary" icon="fas fa-times" class="btn-sm"
                    @click.prevent="clearCard">Clear</jet-button>
            </div>
        </div>
        <div class="card-body">
            <jet-validation-errors />

            <template v-for="cartItem in cartItems" :key="cartItem.id">
                <CartItem :item="cartItem" @quantityChanged="(item, qty) => this.updateItemInCart(item.id, qty)"
                    @deleteItem="(item) => this.removeFromCart(item.id)"
                    @increment="(item) => this.updateItemInCart(item.id, ++item.qty)"
                    @decrement="(item) => this.updateItemInCart(item.id, --item.qty)" @setPrice="setUserPrice">
                </CartItem>
            </template>

            <p class="mb-0 text-right">Subtotal: {{ formatDollars(cartTotals.subtotal) }}</p>
            <p v-if="backorderSubtotal > 0" class="mb-0 text-right">Backorder Subtotal: {{ formatDollars(cartTotals.backorderSubtotal) }}</p>
            <p v-if="futureSubtotal > 0" class="mb-0 text-right">Future Subtotal: {{ formatDollars(cartTotals.futureSubtotal) }}</p>

            <ul class="list-list-group list-group-flush p-0 mt-3">
                <li v-for="lineItem in cartLineItems" :key="lineItem.id" class="list-group-item px-2">
                    <div class="px-0 d-flex align-items-center justify-content-between">
                        <p class="mb-0 font-weight-bold d-flex align-items-center">
                            {{ lineItem.description }}<span v-if="lineItem.qty > 1"> ({{ lineItem.qty }})</span>:
                            <span class="badge badge-secondary py-1 ml-1">{{ formatDollars(lineItem.total) }}</span>
                        </p>
                        <i @click="removeLineItem(lineItem.id)" title="Remove line item"
                            class="fa fa-times text-danger ml-2 ml-lg-5 cursor-pointer"></i>
                    </div>
                </li>
            </ul>

            <div v-if="!external">
                <p v-if="discountTotal" class="text-secondary mb-1 text-size-small">
                    Discount: -{{ formatDollars(discountTotal) }}
                    <span v-if="percentOrderDiscount">({{ percentOrderDiscount }}%)</span>
                </p>

                <jet-button class="btn-sm" icon="fas fa-plus" @click="showDiscountInput = !showDiscountInput">Add
                    Discount</jet-button>
            </div>

            <div v-show="showDiscountInput" class="mt-3">
                <ul class="nav nav-tabs nav-fill" role="tablist">
                    <li class="nav-item" role="presentation">
                        <a class="nav-link active" id="fixed-rate-discount-tab" data-toggle="tab"
                            href="#fixed-rate-discount-tab-content" role="tab" @click="tempPercentOrderDiscount = null">
                            Fixed
                        </a>
                    </li>
                    <li class="nav-item" role="presentation">
                        <a class="nav-link" id="percent-rate-discount-tab" data-toggle="tab"
                            href="#percent-rate-discount-tab-content" role="tab" @click="tempFixedOrderDiscount = null">
                            Percent
                        </a>
                    </li>
                </ul>
                <div class="tab-content bg-light p-3 border-right border-bottom border-left" id="myTabContent">
                    <div class="tab-pane fade show active" id="fixed-rate-discount-tab-content" role="tabpanel">
                        <AmountInput v-model="tempFixedOrderDiscount" ref="fixedDiscountInput"
                            inputPlaceholder="Fixed Discount Amount" />
                    </div>
                    <div class="tab-pane fade" id="percent-rate-discount-tab-content" role="tabpanel">
                        <div class="input-group mb-3">
                            <div class="input-group-prepend">
                                <span class="input-group-text">%</span>
                            </div>
                            <input class="form-control" type="number" ref="percentDiscountInput"
                                v-model="tempPercentOrderDiscount" placeholder="Percent Discount">
                        </div>
                    </div>

                    <jet-button btnStyle="success" class="btn-sm mr-2" @click="updateDiscount">Set Discount</jet-button>
                    <jet-button btnStyle="secondary" class="btn-sm" @click="cancelChanges">Cancel</jet-button>
                    <jet-button btnStyle="danger" icon="fas fa-times" class="float-right btn-sm"
                        @click="removeDiscount">Remove</jet-button>
                </div>
            </div>

            <template v-if="!external">
                <hr>

                <jet-button class="px-5" btnStyle="success" icon="fas fa-shopping-cart" :disable="processing"
                    @click="goToCheckout">Checkout</jet-button>
            </template>
        </div>
    </div>

    <jet-dialog-modal id="add-line-item-modal">
        <template #title>Add Line Item</template>
        <template #content>
            <jet-label for="add-line-item-description" value="Description:" />
            <Multiselect v-model="formAddLineItem.description" :options="lineItemDescriptions" :searchable="true"
                :createTag="true" class="admin-multiselect mb-3" id="add-line-item-description" />

            <AmountInput v-model="formAddLineItem.amount" inputPlaceholder="Amount" />
        </template>

        <template #footer>
            <jet-button @click="addLineItem">Add</jet-button>
            <jet-danger-button class="ml-2" @click="hideAddLineItemModal">Dismiss</jet-danger-button>
        </template>
    </jet-dialog-modal>
</template>

<script>
import CartItem from '@/Components/Admin/CartItem'
import JetValidationErrors from '@/Jetstream/ValidationErrors'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetButton from '@/Jetstream/Button'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetLabel from '@/Jetstream/Label'
import Multiselect from '@vueform/multiselect'
import AmountInput from '@/Shared/AmountInput'

export default {
    components: {
        CartItem,
        JetValidationErrors,
        JetDialogModal,
        JetButton,
        JetDangerButton,
        JetLabel,
        Multiselect,
        AmountInput
    },
    props: {
        mtdId: Number,
        selectedProducts: Array,
        external: {
            type: Boolean,
            default: false
        },
        lineItemDescriptions: Array,
    },
    emits: ['updateSelectedProducts'],
    data() {
        return {
            processing: false,
            cartItems: [],
            cartLineItems: [],
            cartTotals: [],
            showDiscountInput: false,
            fixedOrderDiscount: null,
            tempFixedOrderDiscount: null,
            percentOrderDiscount: null,
            tempPercentOrderDiscount: null,
            formAddItem: this.$inertia.form({
                product_id: null,
            }),
            formUpdateItem: this.$inertia.form({
                item_id: null,
                qty: null,
            }),
            formDeleteItem: this.$inertia.form({
                item_id: null,
            }),
            formUserPrice: this.$inertia.form({
                item_id: null,
                discount_price: null,
            }),
            formSetCartDiscount: this.$inertia.form({
                discount: null,
                type: null,
            }),
            formRemoveCartDiscount: this.$inertia.form({}),
            formAddLineItem: this.$inertia.form({
                description: null,
                amount: null,
            }),
            formRemoveLineItem: this.$inertia.form({
                line_item_id: null,
            }),
        }
    },
    mounted() {
        this.getCart();
    },
    watch: {
        selectedProducts() {
            let cartItemProductIds = this.cartItems.map((cartItem) => cartItem.product_id);
            this.selectedProducts.map((item) => {
                if (!cartItemProductIds.includes(item.id)) {
                    this.addToCart(item);
                }
            });
        },
        showDiscountInput() {
            if (this.showDiscountInput) {
                this.$nextTick(() => {
                    if (this.percentOrderDiscount) {
                        this.$refs.percentDiscountInput.focus();
                    } else {
                        this.$refs.fixedDiscountInput.$refs['amount_input'].focus();
                    }
                });
            }
        },
        fixedOrderDiscount() {
            this.tempFixedOrderDiscount = this.fixedOrderDiscount;
        },
        percentOrderDiscount() {
            this.tempPercentOrderDiscount = this.percentOrderDiscount;
        },
    },
    methods: {
        addToCart(product) {
            this.formAddItem.product_id = product.id;

            this.formAddItem.post(route('admin.mtd.cart.addItem', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => this.getCart(),
            });
        },
        removeFromCart(cartItemId) {
            if (this.formDeleteItem.processing) return;

            this.formDeleteItem.item_id = cartItemId;

            this.formDeleteItem.post(route('admin.mtd.cart.deleteItem', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => this.getCart(),
            });
        },
        updateItemInCart(cartItemId, qty) {
            this.formUpdateItem.item_id = cartItemId;
            this.formUpdateItem.qty = qty;

            this.formUpdateItem.post(route('admin.mtd.cart.updateItem', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => this.getCart(),
            });
        },
        setUserPrice(item, newPrice) {
            this.formUserPrice.item_id = item.id;
            this.formUserPrice.discount_price = newPrice;

            this.formUserPrice.post(route('admin.mtd.cart.setItemDiscount', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => this.getCart(),
            });
        },
        updateDiscount() {
            if (this.tempFixedOrderDiscount) {
                if (this.tempFixedOrderDiscount > this.subtotal) {
                    toastr.error('Fixed discount cannot be greater than subtotal.');
                    return;
                }
            } else if (this.tempPercentOrderDiscount >= 100 || this.tempPercentOrderDiscount < 1) {
                toastr.error('Percent discount must be greater than 1 and less than 100.');
                return;
            }

            this.showDiscountInput = false;

            this.formSetCartDiscount.discount = this.tempFixedOrderDiscount
                ? this.tempFixedOrderDiscount
                : this.tempPercentOrderDiscount;
            this.formSetCartDiscount.type = this.tempFixedOrderDiscount ? 'Fixed' : 'Percent';

            this.formSetCartDiscount.post(route('admin.mtd.cart.setCartDiscount', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => {
                    this.getCart();
                    this.fixedOrderDiscount = this.tempFixedOrderDiscount;
                    this.percentOrderDiscount = this.tempPercentOrderDiscount;
                },
                onError: () => this.resetDiscountInputs(),
            });
        },
        removeDiscount() {
            this.showDiscountInput = false;

            this.formRemoveCartDiscount.post(route('admin.mtd.cart.removeCartDiscount', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => {
                    this.getCart();
                    this.fixedOrderDiscount = this.tempFixedOrderDiscount;
                    this.percentOrderDiscount = this.tempPercentOrderDiscount;
                },
                onError: () => this.resetDiscountInputs(),
            });
        },
        cancelChanges() {
            this.showDiscountInput = false;
            this.resetDiscountInputs();
        },
        resetDiscountInputs() {
            this.tempFixedOrderDiscount = this.fixedOrderDiscount;
            this.tempPercentOrderDiscount = this.percentOrderDiscount;
        },
        updateCartItems(cart) {
            this.cartItems = cart.items.map((item) => {
                return {
                    id: item.id,
                    product_id: item.product.id,
                    name: item.product.name,
                    sku: item.product.sku,
                    img_url: item.image_url,
                    qty: item.qty,
                    price: item.product.productPrice.price,
                    original_price: item.product.productPrice.regularPrice,
                    is_active: item.product.is_active,
                    is_bundle: item?.includes?.length > 0,
                }
            });

            this.cartLineItems = cart.line_items;

            if (cart.discount.type === 'Percent') {
                this.percentOrderDiscount = cart.discount.amount;
            } else {
                this.fixedOrderDiscount = cart.discount.amount;
            }

            this.cartTotals = cart.cartTotals

            let productIds = this.cartItems.map((item) => item.product_id);
            this.$emit('updateSelectedProducts', productIds);
        },
        getCart() {
            axios
                .get(route('admin.mtd.cart', this.mtdId))
                .then((resp) => {
                    this.updateCartItems(resp.data);
                });
        },
        clearCard() {
            this.$inertia.form({}).delete(route('admin.mtd.cart', this.mtdId), {
                onSuccess: () => this.getCart()
            });
        },
        goToCheckout() {
            if (this.cartItems.length > 0) {
                this.processing = true;
                this.$inertia.visit(route('admin.mtd.checkout', this.mtdId), {
                    onError: () => this.processing = false,
                });
            }
        },
        showAddLineItemModal() {
            $('#add-line-item-modal').modal('show');
        },
        hideAddLineItemModal() {
            $('#add-line-item-modal').modal('hide');
        },
        addLineItem() {
            if (this.formAddLineItem.processing) return;

            this.formAddLineItem.post(route('admin.mtd.cart.addLineItem', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => {
                    this.getCart();
                    this.hideAddLineItemModal();
                    this.formAddLineItem.reset();
                }
            });
        },
        removeLineItem(id) {
            if (this.formRemoveLineItem.processing) return;

            this.formRemoveLineItem.line_item_id = id;
            this.formRemoveLineItem.delete(route('admin.mtd.cart.removeLineItem', this.mtdId), {
                preserveScroll: true,
                onSuccess: () => this.getCart(),
            });
        }
    }
}
</script>
